/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/

import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert } from '@mui/material';
import Button from 'components/generals/Button';
import DownloadIcon from '@mui/icons-material/Download';
import { renderAsync } from 'docx-preview';
import ExcelJS from 'exceljs';
import { useTranslation } from 'react-i18next';

interface OfficeDocumentRendererProps {
  originalUrl: string;  
  filename: string;
  mimetype: string;
}

export const OfficeDocumentRenderer: React.FC<OfficeDocumentRendererProps> = ({
  originalUrl,
  filename,
  mimetype
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [isWordDoc, setIsWordDoc] = useState(false);
  const [wordBuffer, setWordBuffer] = useState<ArrayBuffer | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadDocument = async () => {
      try {
        setLoading(true);
        
        // Wait for blob URL to be ready
        if (!originalUrl || originalUrl === '') {
          return;
        }
        
        const response = await fetch(originalUrl);
        const arrayBuffer = await response.arrayBuffer();
        
        if (arrayBuffer.byteLength === 0) {
          throw new Error(t('documentViewer.downloadedFileEmpty'));
        }
        
        if (mimetype.includes('word') || filename.endsWith('.docx')) {
          setIsWordDoc(true);
          setWordBuffer(arrayBuffer);
          setLoading(false);
        }
        else if (mimetype.includes('spreadsheet') || mimetype.includes('excel') || filename.endsWith('.xlsx')) {
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(arrayBuffer);
          
          let html = '<div style="padding: 20px; font-family: Arial, sans-serif;">';
          
          workbook.worksheets.forEach((worksheet, index) => {
            html += `<h2 style="color: #1976d2; margin-top: ${index > 0 ? '40px' : '0'};">${worksheet.name}</h2>`;
            html += '<table style="border-collapse: collapse; width: 100%; margin-bottom: 30px; background: white; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">';
            
            let isFirstRow = true;
            worksheet.eachRow((row, rowNumber) => {
              html += '<tr>';
              row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                const tag = isFirstRow ? 'th' : 'td';
                const styles = isFirstRow 
                  ? 'background: #1976d2; color: white; font-weight: bold; padding: 12px; border: 1px solid #ddd; text-align: left;'
                  : 'padding: 10px; border: 1px solid #ddd; text-align: left;';
                html += `<${tag} style="${styles}">${cell.text || ''}</${tag}>`;
              });
              html += '</tr>';
              isFirstRow = false;
            });
            
            html += '</table>';
          });
          
          html += '</div>';
          setHtmlContent(html);
          setLoading(false);
        }
        else {
          throw new Error(t('documentViewer.fileTypeRequiresDownload'));
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : t('documentViewer.errorLoadingDocument'));
        setLoading(false);
      }
    };

    loadDocument();
  }, [originalUrl, filename, mimetype, t]);

  useEffect(() => {
    const renderWordDocument = async () => {
      if (wordBuffer && containerRef.current && isWordDoc) {
        try {
          containerRef.current.innerHTML = '';
          
          await renderAsync(wordBuffer, containerRef.current, undefined, {
            className: 'docx-preview',
            inWrapper: false,
            ignoreWidth: false,
            ignoreHeight: false,
            ignoreFonts: false,
            breakPages: true,
            ignoreLastRenderedPageBreak: true,
            experimental: false,
            trimXmlDeclaration: true,
            useBase64URL: false,
            renderHeaders: true,
            renderFooters: true,
            renderFootnotes: true,
            renderEndnotes: true
          });
        } catch (err) {
          setError(err instanceof Error ? err.message : t('documentViewer.errorRenderingWord'));
        }
      }
    };

    renderWordDocument();
  }, [wordBuffer, isWordDoc, t]);

  const handleDownloadOriginal = async () => {
    try {
      setDownloading(true);
      
      const response = await fetch(originalUrl);
      const blob = await response.blob();
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      setError(t('documentViewer.errorDownloading'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box className="office-document-renderer">
      {loading && (
        <Box className="loading-container">
          <CircularProgress size={60} />
          <Typography variant="body1" color="text.secondary">
            {t('documentViewer.loadingDocumentPreview')}
          </Typography>
        </Box>
      )}

      {error && (
        <Box className="error-container">
          <Alert severity="warning" className="alert">
            <Typography variant="body1" gutterBottom>
              <strong>{t('documentViewer.cannotDisplayPreview')}</strong>
            </Typography>
            <Typography variant="body2">{error}</Typography>
          </Alert>
          <Button
            variant="contained"
            size="large"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadOriginal}
            disabled={downloading}
          >
            {downloading ? t('documentViewer.downloading') : t('documentViewer.downloadFile')}
          </Button>
        </Box>
      )}

      {!loading && !error && (
        <>
          <Paper className="header" elevation={2}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {filename}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('documentViewer.browserRenderedPreview')}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadOriginal}
              disabled={downloading}
            >
              {downloading ? t('documentViewer.downloading') : t('documentViewer.downloadOriginal')}
            </Button>
          </Paper>

          <Box className="content">
            <Paper className="preview" elevation={3}>
              {isWordDoc ? (
                <Box ref={containerRef} className="docx-container" />
              ) : (
                <Box
                  className="html-content"
                  dangerouslySetInnerHTML={{ __html: htmlContent }}
                />
              )}
            </Paper>
          </Box>
        </>
      )}
    </Box>
  );
};
